import {Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {WorkflowInstanceDto} from '../../api/model/workflowInstanceDto';
import {StepLogDto} from '../../api/model/stepLogDto';
import {ModuleManifest} from '../../api/model/moduleManifest';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faCircleCheck,
  faCircleExclamation,
  faCircleXmark,
  faClockRotateLeft,
  faSpinner,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import {WorkflowService} from '../../services/workflow.service';

export interface LogEntry {
  timestamp?: string;
  level: 'INFO' | 'WARNING' | 'ERROR' | 'DEBUG';
  title: string;
  message: string;
}

@Component({
  selector: 'app-workflow-test-panel',
  standalone: true,
  imports: [FaIconComponent],
  host: {'class': 'flex flex-col h-full min-h-0'},
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <!-- Header -->
    <div class="flex items-center gap-2 px-4 py-2 border-b border-gray-200">
      <span class="flex-1 text-base font-medium text-gray-900">Workflow Live Test</span>
      <button (click)="closed.emit()" class="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100">
        <fa-icon [icon]="faXmark" class="text-sm text-gray-500"></fa-icon>
      </button>
    </div>

    <!-- Status Banner -->
    <div class="flex items-center gap-4 px-4 py-2 border-b border-gray-200"
         [class.bg-green-50]="instance?.status === 'COMPLETED'"
         [class.bg-red-50]="instance?.status === 'FAILED' || instance?.status === 'CANCELLED'"
         [class.bg-gray-100]="instance?.status === 'RUNNING' || instance?.status === 'PENDING'">
      @if (instance?.status === 'COMPLETED') {
        <fa-icon [icon]="faCircleCheck" class="text-base text-green-500"></fa-icon>
      } @else if (instance?.status === 'FAILED' || instance?.status === 'CANCELLED') {
        <fa-icon [icon]="faCircleXmark" class="text-base text-red-500"></fa-icon>
      } @else {
        <fa-icon [icon]="faSpinner" class="text-base text-gray-500 animate-spin"></fa-icon>
      }
      <div class="flex flex-col">
        <span class="text-sm font-medium text-gray-900">{{ statusLabel }}</span>
        <span class="text-xs text-gray-500">{{ durationLabel }}</span>
      </div>
    </div>

    <!-- Module und Status Header -->
    <div class="flex items-center px-4 py-2 bg-gray-100 border-b border-gray-200">
      <span class="text-sm font-medium text-gray-900">Module und Status</span>
    </div>

    <!-- Step list -->
    <div class="flex-1 overflow-y-auto">
      @for (step of instance?.steps; track step.nodeId) {
        <!-- Step row (clickable) -->
        <div (click)="toggleStep(step.nodeId!)"
             class="flex items-center gap-4 px-4 py-2 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
          <!-- Expand chevron -->
          <fa-icon [icon]="expandedStepId === step.nodeId ? faChevronDown : faChevronRight"
                   class="text-[10px] text-gray-400 shrink-0 w-3"></fa-icon>

          <!-- Status Icon -->
          @if (step.status === 'COMPLETED') {
            <fa-icon [icon]="faCircleCheck" class="text-base text-green-500 shrink-0"></fa-icon>
          } @else if (step.status === 'FAILED') {
            <fa-icon [icon]="faCircleXmark" class="text-base text-red-500 shrink-0"></fa-icon>
          } @else if (step.status === 'RUNNING') {
            <fa-icon [icon]="faSpinner" class="text-base text-gray-500 animate-spin shrink-0"></fa-icon>
          } @else {
            <fa-icon [icon]="faCircleExclamation" class="text-base text-yellow-500 shrink-0"></fa-icon>
          }

          <!-- Module info -->
          <div class="flex flex-col flex-1 min-w-0">
            <span class="text-sm font-medium text-gray-900 truncate">{{ getModuleName(step) }}</span>
            <span class="text-xs text-gray-500 truncate">{{ step.statusMessage || '' }}</span>
          </div>

          <!-- Badges -->
          <div class="flex items-center gap-1 shrink-0">
            @if (getStepDuration(step)) {
              <span class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-gray-200 text-xs font-medium text-gray-700">
                <fa-icon [icon]="faClockRotateLeft" class="text-[10px]"></fa-icon>
                {{ getStepDuration(step) }}
              </span>
            }
            <span class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-medium"
                  [class.bg-green-50]="step.status === 'COMPLETED'"
                  [class.text-green-600]="step.status === 'COMPLETED'"
                  [class.bg-red-50]="step.status === 'FAILED'"
                  [class.text-red-600]="step.status === 'FAILED'"
                  [class.bg-yellow-100]="step.status === 'RUNNING' || step.status === 'PENDING'"
                  [class.text-yellow-700]="step.status === 'RUNNING' || step.status === 'PENDING'">
              @if (step.status === 'COMPLETED') {
                <fa-icon [icon]="faCheck" class="text-[10px]"></fa-icon>
                Erfolg
              } @else if (step.status === 'FAILED') {
                <fa-icon [icon]="faCircleXmark" class="text-[10px]"></fa-icon>
                Fehler
              } @else {
                <fa-icon [icon]="faCircleExclamation" class="text-[10px]"></fa-icon>
                Läuft
              }
            </span>
          </div>
        </div>

        <!-- Expanded detail panel -->
        @if (expandedStepId === step.nodeId) {
          <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <!-- Input Data -->
            @if (step.inputData && hasEntries(step.inputData)) {
              <div class="mb-3">
                <span class="text-[11px] font-medium text-gray-500 uppercase">Eingaben</span>
                <div class="mt-1 flex flex-col gap-1">
                  @for (entry of getDisplayEntries(step.inputData); track entry.key) {
                    <div class="flex gap-2 text-xs">
                      <span class="font-medium text-gray-700 shrink-0 min-w-[100px]">{{ entry.key }}:</span>
                      <span class="text-gray-600 truncate">{{ entry.value }}</span>
                    </div>
                  }
                </div>
              </div>
            }

            <!-- Output Data -->
            @if (step.outputData && hasEntries(step.outputData)) {
              <div class="mb-3">
                <span class="text-[11px] font-medium text-gray-500 uppercase">Ausgaben</span>
                <div class="mt-1 flex flex-col gap-1">
                  @for (entry of getDisplayEntries(step.outputData); track entry.key) {
                    <div class="flex gap-2 text-xs">
                      <span class="font-medium text-gray-700 shrink-0 min-w-[100px]">{{ entry.key }}:</span>
                      <span class="text-gray-600 truncate">{{ entry.value }}</span>
                    </div>
                  }
                </div>
              </div>
            }

            <!-- Hybrid Child Instances -->
            @if (isHybridExecutor(step)) {
              <div class="mt-2">
                <span class="text-[11px] font-medium text-gray-500 uppercase">Prüfregel-Ergebnisse</span>
                @if (childInstancesLoading) {
                  <div class="mt-1 flex items-center gap-2 text-xs text-gray-500">
                    <fa-icon [icon]="faSpinner" class="animate-spin"></fa-icon>
                    Lade Regel-Details…
                  </div>
                } @else if (_childInstances.length > 0) {
                  <div class="mt-1 flex flex-col gap-2">
                    @for (child of _childInstances; track child.id) {
                      <div class="rounded border border-gray-200 bg-white overflow-hidden">
                        <!-- Child header -->
                        <div (click)="toggleChild(child.id!)"
                           class="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50">
                          <fa-icon [icon]="expandedChildId === child.id ? faChevronDown : faChevronRight"
                                 class="text-[10px] text-gray-400 w-3"></fa-icon>
                          @if (child.status === 'COMPLETED') {
                          <fa-icon [icon]="faCircleCheck" class="text-sm text-green-500"></fa-icon>
                          } @else if (child.status === 'FAILED' || child.status === 'CANCELLED') {
                          <fa-icon [icon]="faCircleXmark" class="text-sm text-red-500"></fa-icon>
                          } @else {
                          <fa-icon [icon]="faSpinner" class="text-sm text-gray-500 animate-spin"></fa-icon>
                          }
                          <span class="text-xs font-medium text-gray-900 flex-1 truncate">{{ child.workflowName || 'Prüfregel' }}</span>
                          @if (getChildScore(child) != null) {
                          <span class="text-xs font-medium px-1.5 py-0.5 rounded"
                                [class.bg-green-100]="getChildScore(child)! >= 0.7"
                                [class.text-green-700]="getChildScore(child)! >= 0.7"
                                [class.bg-red-100]="getChildScore(child)! < 0.7"
                                [class.text-red-700]="getChildScore(child)! < 0.7">
                            {{ (getChildScore(child)! * 100).toFixed(0) }}%
                          </span>
                          }
                          <button (click)="onChildDrillDown($event, child)"
                                  class="flex items-center justify-center w-5 h-5 rounded hover:bg-gray-200 shrink-0"
                                  title="Workflow-Details anzeigen">
                            <fa-icon [icon]="faChevronRight" class="text-[10px] text-gray-500"></fa-icon>
                          </button>
                        </div>
                        <!-- Child steps (expanded) -->
                        @if (expandedChildId === child.id && child.steps) {
                          <div class="border-t border-gray-200 bg-gray-50 px-3 py-2">
                          @for (childStep of child.steps; track childStep.nodeId) {
                            <div class="flex items-center gap-2 py-1">
                              @if (childStep.status === 'COMPLETED') {
                                <fa-icon [icon]="faCircleCheck" class="text-xs text-green-500"></fa-icon>
                              } @else if (childStep.status === 'FAILED') {
                                <fa-icon [icon]="faCircleXmark" class="text-xs text-red-500"></fa-icon>
                              } @else {
                                <fa-icon [icon]="faCircleExclamation" class="text-xs text-yellow-500"></fa-icon>
                              }
                              <span class="text-xs text-gray-700 flex-1 truncate">{{ childStep.moduleId || childStep.nodeId }}</span>
                              @if (childStep.status === 'FAILED' && childStep.errorMessage) {
                                <span class="text-xs text-red-600 truncate max-w-[150px]">{{ childStep.errorMessage }}</span>
                              }
                            </div>
                          }
                          <!-- Child output -->
                          @if (child.outputData && hasEntries(child.outputData)) {
                            <div class="mt-2 pt-2 border-t border-gray-200">
                              <span class="text-[10px] font-medium text-gray-500 uppercase">Ergebnis</span>
                              <div class="mt-1 flex flex-col gap-0.5">
                                @for (entry of getDisplayEntries(child.outputData); track entry.key) {
                                  <div class="flex gap-2 text-xs">
                                    <span class="font-medium text-gray-600 min-w-[80px]">{{ entry.key }}:</span>
                                    <span class="text-gray-500 truncate">{{ entry.value }}</span>
                                  </div>
                                }
                              </div>
                            </div>
                          }
                          </div>
                        }
                      </div>
                    }
                  </div>
                } @else {
                  <!-- Fallback: show inline results from output data -->
                  @if (getHybridResults(step).length > 0) {
                    <div class="mt-1 flex flex-col gap-1.5">
                      @for (child of getHybridResults(step); track child.definitionId) {
                        <div class="flex items-center gap-2 px-2 py-1.5 rounded bg-white border border-gray-200">
                          @if (child.status === 'COMPLETED') {
                          <fa-icon [icon]="faCircleCheck" class="text-sm text-green-500 shrink-0"></fa-icon>
                          } @else {
                          <fa-icon [icon]="faCircleXmark" class="text-sm text-red-500 shrink-0"></fa-icon>
                          }
                          <span class="text-xs font-medium text-gray-900 flex-1 truncate">{{ child.name || child.definitionId }}</span>
                          @if (child.score != null) {
                          <span class="text-xs font-medium px-1.5 py-0.5 rounded"
                                [class.bg-green-100]="child.score >= 0.7"
                                [class.text-green-700]="child.score >= 0.7"
                                [class.bg-red-100]="child.score < 0.7"
                                [class.text-red-700]="child.score < 0.7">
                            {{ (child.score * 100).toFixed(0) }}%
                          </span>
                          }
                        </div>
                      }
                    </div>
                  }
                }
              </div>
            }

            <!-- Error message -->
            @if (step.status === 'FAILED' && step.errorMessage) {
              <div class="mt-2 px-2 py-1.5 rounded bg-red-50 border border-red-200">
                <span class="text-xs text-red-700">{{ step.errorMessage }}</span>
              </div>
            }

            <!-- Empty state -->
            @if (!hasEntries(step.inputData) && !hasEntries(step.outputData) && !isHybridExecutor(step)) {
              <span class="text-xs text-gray-400">Keine Daten verfügbar</span>
            }
          </div>
        }
      }

      <!-- Result Summary (when completed) -->
      @if (instance?.status === 'COMPLETED' && instance?.outputData) {
        <div class="mx-4 my-3 p-3 rounded-lg border-2"
             [class.border-green-300]="isOverallSuccess"
             [class.bg-green-50]="isOverallSuccess"
             [class.border-red-300]="!isOverallSuccess"
             [class.bg-red-50]="!isOverallSuccess">
          <div class="flex items-center gap-2 mb-2">
            @if (isOverallSuccess) {
              <fa-icon [icon]="faCircleCheck" class="text-base text-green-600"></fa-icon>
              <span class="text-sm font-medium text-green-800">Prüfung bestanden</span>
            } @else {
              <fa-icon [icon]="faCircleXmark" class="text-base text-red-600"></fa-icon>
              <span class="text-sm font-medium text-red-800">Prüfung nicht bestanden</span>
            }
          </div>
          @if (summaryScore != null) {
            <div class="flex items-center gap-4 text-xs text-gray-700">
              <span>Score: <strong>{{ (summaryScore * 100).toFixed(0) }}%</strong></span>
              @if (summaryRuleCount) {
                <span>Regeln: <strong>{{ summaryRuleCount }}</strong></span>
              }
            </div>
          }
        </div>
      }
    </div>
  `,
})
export class WorkflowTestPanelComponent {
  @Input() instance: WorkflowInstanceDto | null = null;
  @Input() modules: ModuleManifest[] = [];

  @Output() childDrillDown = new EventEmitter<WorkflowInstanceDto>();
  @Output() closed = new EventEmitter<void>();
  expandedStepId: string | null = null;
  expandedChildId: string | null = null;
  childInstancesLoading = false;
  protected readonly faChevronDown = faChevronDown;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faCircleXmark = faCircleXmark;
  protected readonly faClockRotateLeft = faClockRotateLeft;
  protected readonly faCheck = faCheck;
  protected readonly faXmark = faXmark;
  protected readonly faSpinner = faSpinner;
  private workflowService = inject(WorkflowService);
  private childInstancesLoaded = false;

  _childInstances: WorkflowInstanceDto[] = [];

  /** Pre-loaded child instances (optional — if provided, skips lazy loading) */
  @Input() set childInstances(value: WorkflowInstanceDto[]) {
    if (value && value.length > 0) {
      this._childInstances = value;
      this.childInstancesLoaded = true;
      this.childInstancesLoading = false;
    }
  }

  get statusLabel(): string {
    switch (this.instance?.status) {
      case 'COMPLETED':
        return 'Ausführung abgeschlossen';
      case 'FAILED':
        return 'Ausführung fehlgeschlagen';
      case 'CANCELLED':
        return 'Ausführung abgebrochen';
      case 'RUNNING':
        return 'Wird ausgeführt…';
      default:
        return 'Warte auf Start…';
    }
  }

  get durationLabel(): string {
    if (!this.instance?.startedAt) return '';
    const start = new Date(this.instance.startedAt).getTime();
    const end = this.instance.completedAt
      ? new Date(this.instance.completedAt).getTime()
      : Date.now();
    const durationSec = ((end - start) / 1000).toFixed(1);
    const timeAgo = this.instance.completedAt
      ? this.getTimeAgo(new Date(this.instance.completedAt))
      : '';
    return `Dauer: ${durationSec}s` + (timeAgo ? `  ·  Letzte Ausführung: ${timeAgo}` : '');
  }

  get isOverallSuccess(): boolean {
    const out = this.instance?.outputData;
    if (!out) return false;
    if (out['success'] === true) return true;
    if (out['score'] != null) return out['score'] >= 0.7;
    return this.instance?.status === 'COMPLETED';
  }

  get summaryScore(): number | null {
    return this.instance?.outputData?.['score'] ?? null;
  }

  get summaryRuleCount(): number | null {
    return this.instance?.outputData?.['rule_count'] ?? null;
  }

  toggleStep(nodeId: string): void {
    this.expandedStepId = this.expandedStepId === nodeId ? null : nodeId;

    // Load child instances when expanding a HYBRID_RULESET_EXECUTOR step
    if (this.expandedStepId === nodeId && !this.childInstancesLoaded) {
      const step = this.instance?.steps?.find(s => s.nodeId === nodeId);
      if (step && this.isHybridExecutor(step) && this.instance?.id) {
        this.loadChildInstances(this.instance.id);
      }
    }
  }

  toggleChild(childId: string): void {
    this.expandedChildId = this.expandedChildId === childId ? null : childId;
  }

  onChildDrillDown(event: MouseEvent, child: WorkflowInstanceDto): void {
    event.stopPropagation();
    this.childDrillDown.emit(child);
  }

  getModuleName(step: StepLogDto): string {
    if (!step.moduleId) return step.nodeId ?? 'Unbekannt';
    const mod = this.modules.find(m => m.moduleId === step.moduleId);
    return mod?.name ?? step.moduleId;
  }

  getStepDuration(step: StepLogDto): string | null {
    if (!step.startedAt || !step.completedAt) return null;
    const ms = new Date(step.completedAt).getTime() - new Date(step.startedAt).getTime();
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(0)} s`;
  }

  hasEntries(data: any): boolean {
    return data && typeof data === 'object' && Object.keys(data).length > 0;
  }

  getDisplayEntries(data: any): { key: string; value: string }[] {
    if (!data || typeof data !== 'object') return [];
    return Object.entries(data)
      .filter(([key]) => !key.startsWith('__'))
      .map(([key, value]) => ({
        key,
        value: typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''),
      }));
  }

  isHybridExecutor(step: StepLogDto): boolean {
    return step.moduleId === 'HYBRID_RULESET_EXECUTOR';
  }

  getHybridResults(step: StepLogDto): { definitionId: string; name?: string; status: string; score?: number }[] {
    const results = step.outputData?.['results'];
    if (!Array.isArray(results)) return [];
    return results;
  }

  getChildScore(child: WorkflowInstanceDto): number | null {
    return child.outputData?.['score'] ?? null;
  }

  private loadChildInstances(parentId: string): void {
    this.childInstancesLoading = true;
    this.workflowService.getChildInstances(parentId).subscribe({
      next: (children) => {
        this._childInstances = children;
        this.childInstancesLoaded = true;
        this.childInstancesLoading = false;
      },
      error: () => {
        this.childInstancesLoading = false;
      }
    });
  }

  private getTimeAgo(date: Date): string {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return `vor ${seconds} Sek.`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `vor ${minutes} Min.`;
    return `vor ${Math.floor(minutes / 60)} Std.`;
  }
}
